import { Calendar, File, House, Image, User } from "lucide-react";
import React from "react";
import { Link } from "../ui/Link";
import { useFileDownload } from "./useFileDownload";

interface FileResult {
  id: string;
  name: string;
  url: string;
  typeIcon: string;
}

interface DiaryItem {
  diaryID: string;
  startDate: string;
  endDate?: string;
  typeID: string;
  description?: string;
  contactID?: string;
  contactName?: string;
  propertyID?: string;
  propertyName?: string;
  fileResults: FileResult[];
}

interface OMSDiaryNoteItemDisplayProps {
  Item: DiaryItem;
  DNType: number; // 1:Contact, 2:Property, 0: Both
  IsUpcomingEvent?: boolean;
  onActionFunc?: (diaryID: string, isDelete: boolean) => void;
  ReadOnly?: boolean;
}

const imageFileTypes = ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "ico", "webp"];

export const OMSDiaryNoteItemDisplay: React.FC<OMSDiaryNoteItemDisplayProps> = ({ Item, DNType, IsUpcomingEvent = false, onActionFunc, ReadOnly = false }) => {
  const { downloadFile } = useFileDownload();

  const onDisplayTitle = (): string => {
    if (IsUpcomingEvent) {
      let diaryTitle = Item.startDate;
      if (Item.endDate) {
        diaryTitle = `${diaryTitle}-${Item.endDate.substring(10)}`;
      }
      diaryTitle += ` ${Item.typeID}`;
      return diaryTitle;
    } else {
      let diaryTitle = Item.startDate.substring(10);
      if (Item.endDate) {
        diaryTitle = `${diaryTitle}-${Item.endDate.substring(10)}`;
      }
      diaryTitle += ` ${Item.typeID}`;
      return diaryTitle;
    }
  };

  const onUpdateClick = (): void => {
    if (!ReadOnly && onActionFunc) {
      onActionFunc(Item.diaryID, false);
    }
  };

  return (
    <div className="tw-w-full tw-max-w-full">
      <div className="tw-flex tw-max-w-full tw-gap-3 tw-p-2">
        <div className="tw-flex-shrink-0">
          <Calendar size={20} />
        </div>

        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <Link onClick={onUpdateClick} variant="bold" asChild>
            <div>{onDisplayTitle()}</div>
          </Link>

          {Item.description && <div className="tw-overflow-hidden tw-text-ellipsis">{Item.description}</div>}

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {DNType !== 1 && Item.contactID && (
              <Link to={`/Contacts/SaveContact?ID=${Item.contactID}`} className="tw-flex tw-items-center tw-gap-1">
                <User className="tw-size-4 tw-min-h-4 tw-min-w-4" />
                <span className="tw-overflow-hidden tw-text-ellipsis">{Item.contactName}</span>
              </Link>
            )}

            {DNType !== 2 && Item.propertyID && (
              <Link to={`/Property/SaveProperty?ID=${Item.propertyID}`} className="tw-flex tw-items-center tw-gap-1">
                <House className="tw-size-4 tw-min-h-4 tw-min-w-4" />
                <span className="tw-overflow-hidden tw-text-ellipsis">{Item.propertyName}</span>
              </Link>
            )}

            {Item.fileResults.map((f) => (
              <Link asChild className="tw-flex tw-min-w-0 tw-items-center tw-gap-1" key={f.id}>
                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1" onClick={() => downloadFile(f.id, f.name)}>
                  {imageFileTypes.includes(f.name.toLowerCase().split(".").pop() || "") ? (
                    <Image className="tw-size-4 tw-min-h-4 tw-min-w-4" />
                  ) : (
                    <File className="tw-size-4 tw-min-h-4 tw-min-w-4" />
                  )}
                  <span className="tw-overflow-hidden tw-text-ellipsis">{f.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
